import { LineMessage, LineMessageItemInput, LineMessageType } from '@anyx/common-graphql';
import { TrackingUrlArray } from '@anyx/feature-crm/shared';

import { LineDisplayMessageBlock, LineMessageBlock, LineMessageBlockType } from '../components';

import {
  RichMessageWithImage,
  buttonAdapter,
  imagesAdapter,
  plainTextAdapter,
  richMessageAdapter,
} from './message-adapters';

export const setMessagesToGQLLineContent = (
  messages: LineMessageBlock[]
): LineMessageItemInput[] => {
  return messages.map((message) => {
    if (message.type === 'PLAIN_TEXT') {
      return {
        type: LineMessageType.PLAIN_TEXT,
        ...plainTextAdapter.toGQL(message),
      };
    }

    if (message.type === 'RICH') {
      return {
        type: LineMessageType.RICH,
        ...richMessageAdapter.toGQL(message as RichMessageWithImage),
      };
    }

    if (message.type === 'IMAGES') {
      return {
        type: LineMessageType.IMAGES,
        ...imagesAdapter.toGQL(message),
      };
    }

    if (message.type === 'BUTTON') {
      return {
        type: LineMessageType.BUTTON,
        ...buttonAdapter.toGQL(message),
      };
    }

    return {
      type: message.type as LineMessageType,
    };
  });
};

export const setMessagesToGQLOriginalUrls = (
  messages: LineMessageBlock[],
  trackingUrls: TrackingUrlArray = []
): string[] => {
  const urls = messages.reduce((acc, message) => {
    if (message.type === 'PLAIN_TEXT') {
      return [...acc, ...plainTextAdapter.toOriginalUrls(message)];
    }

    if (message.type === 'RICH' && message.image) {
      return [
        ...acc,
        ...richMessageAdapter.toOriginalUrls(message as RichMessageWithImage, trackingUrls),
      ];
    }

    if (message.type === 'IMAGES') {
      return [...acc, ...imagesAdapter.toOriginalUrls(message, trackingUrls)];
    }

    if (message.type === 'BUTTON') {
      return [...acc, ...buttonAdapter.toOriginalUrls(message, trackingUrls)];
    }

    return acc;
  }, [] as string[]);

  const uniqueUrls = Array.from(new Set(urls));

  return uniqueUrls;
};

export const setMessagesForSingleCard = (message: LineMessage): LineDisplayMessageBlock => {
  switch (message.__typename) {
    case 'PlainTextMessage':
      return plainTextAdapter.toMessageBlock(message);
    case 'RichMessage':
      return richMessageAdapter.toMessageBlock(message);
    case 'ImageMessage':
      return imagesAdapter.toMessageBlock(message);
    case 'ButtonMessage':
      return buttonAdapter.toMessageBlock(message);
    case 'SingleImageMessage':
      return {
        type: 'SINGLE_IMAGE',
        originalUrl: message.originalUrl,
        previewUrl: message.previewUrl,
      };
    case 'FileMessage':
      // Render FileMessage.card using the same ButtonType rendering
      return buttonAdapter.toMessageBlock({ cards: [message.card] });
    default:
      return {
        type: LineMessageType.PLAIN_TEXT,
        text: '',
      };
  }
};

export const setMessagesForCards = (content: LineMessage[]): LineMessageBlock[] => {
  return content
    .map(setMessagesForSingleCard)
    .filter((block): block is LineMessageBlock =>
      LineMessageBlockType.safeParse(block.type).success
    );
};
